import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    AlertTriangle,
    CheckCircle2,
    ChevronRight,
    ShieldAlert,
    Timer,
    CalendarDays,
} from 'lucide-react';
import { OvertimeProgressBar } from '../UI/OvertimeProgressBar';
import { formatDate } from '../../utils/dateUtils';

// ---------------------------------------------------------------------------
// Deep-link highlight handler — for /team-dashboard?tab=overview&highlight=<staff_id>
// ---------------------------------------------------------------------------

/**
 * Window (in ms) during which a deep-linked row is visually highlighted
 * after the watchlist mounts. The ring + soft pulse fades automatically
 * after this so the highlight feels like a transient "here it is" cue,
 * not a permanent state the user has to dismiss.
 *
 * Sized to 6 s based on the locked plan: long enough to survive the
 * post-navigation reflow + any scroll animation, short enough to not
 * linger in the viewport if the user has already taken action.
 */
const HIGHLIGHT_DURATION_MS = 6000;

/**
 * Read the deep-link highlight target from the URL.
 *
 * Watchlist emails (``notify_rest_day_ro_chain_alert`` from Deliverable
 * 2, and the planned consolidated compliance digest) embed a CTA of
 * the form ``/team-dashboard?tab=overview&highlight=W0029``.
 * Recipients land on the Overview tab and expect the worker referenced
 * in the email to be immediately legible — without scrolling, without
 * scanning through the card.
 *
 * Returns the staff_id string (e.g. ``"W0029"``) for the first
 * ``HIGHLIGHT_DURATION_MS`` after mount, then ``null``. The transient
 * value drives both the auto-expand (if the worker would otherwise
 * be hidden below the "Show N more" fold) and the visual ring on
 * the matching row.
 *
 * Falsy / SSR-safe: returns ``null`` if ``window.location`` is not
 * available or the param is missing.
 */
const useHighlightedStaffId = () => {
    const [highlightId, setHighlightId] = useState(null);

    useEffect(() => {
        let initial = null;
        try {
            if (typeof window !== 'undefined' && window.location) {
                const params = new URLSearchParams(window.location.search);
                initial = params.get('highlight');
            }
        } catch {
            initial = null;
        }
        if (!initial) return undefined;
        setHighlightId(initial);
        const timer = window.setTimeout(() => {
            setHighlightId(null);
        }, HIGHLIGHT_DURATION_MS);
        return () => window.clearTimeout(timer);
    }, []);

    return highlightId;
};

// ---------------------------------------------------------------------------
// Eligibility predicates — what counts as "at risk" for each surface
// ---------------------------------------------------------------------------

/**
 * A worker is on the 72h OT Watchlist when ANY of the following holds:
 *
 * 1. ``current_ot_hours >= 48`` — they've already crossed the formal MOM
 *    "at risk" threshold (the same cutoff the admin /overtime-monitoring
 *    page uses; see ``staff_core/overtime_api_views.get_risk_level``).
 * 2. ``ot_forecast_status in ('projected_exceed', 'watch')`` — trajectory
 *    has them landing >60h by month-end even if they haven't hit 48h yet.
 *    The two-factor (frequency × intensity) forecast from
 *    ``calculate_ot_forecast`` catches the fast-accelerating cases that
 *    a current-hours-only check would miss.
 * 3. ``ot_compliance`` is non-compliant — backup signal in case the
 *    backend re-categorises a worker that the helper missed (kept for
 *    forward-compat with future tier additions).
 *
 * Staff (non-Worker) members are never eligible — the cap applies only
 * to Workers under MOM Employment Act Part IV.
 */
const isOtWatchlistMember = (m) => {
    if (m.classification !== 'worker') return false;
    if ((m.current_ot_hours ?? 0) >= 48) return true;
    if (['projected_exceed', 'watch'].includes(m.ot_forecast_status)) return true;
    if (['at_risk', 'critical', 'non_compliant'].includes(m.ot_compliance)) return true;
    return false;
};

/**
 * A member is on the Rest Day Watchlist when EITHER:
 *
 * 1. ``rest_day_compliance`` is past the ``watch`` tier — i.e. backend has
 *    promoted the streak into the formal warning ladder.
 * 2. ``consecutive_working_days >= 7`` — defensive fallback so a streak
 *    in its 7th day shows up even before the daily 07:30 SGT compliance
 *    sweep has re-classified the record (typical staleness window: <1
 *    business day on the day of the sweep, longer over weekends).
 *
 * Applies to BOTH Workers and Staff — rest-day rules under MOM s36 apply
 * to all operational employees, not just Workers.
 */
const isRestDayWatchlistMember = (m) => {
    const status = m.rest_day_compliance;
    if (['watch', 'warning', 'critical', 'non_compliant'].includes(status)) return true;
    if ((m.consecutive_working_days ?? 0) >= 7) return true;
    return false;
};

// ---------------------------------------------------------------------------
// Helpers — countdown / colour resolution
// ---------------------------------------------------------------------------

/**
 * Days remaining (inclusive) between today and the breach date in SGT.
 * Returns ``null`` when no breach is forecast. Negative values are
 * clamped to ``0`` (the breach has already arrived per the latest
 * forecast — the bar is already showing the >72h colour anyway).
 */
const daysUntil = (isoDate) => {
    if (!isoDate) return null;
    const target = new Date(`${isoDate}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
};

/**
 * Resolve the visual tone for the rest-day pip strip + count badge.
 *
 * The four severity tiers mirror ``EmployeeRestDayCompliance``:
 *   - compliant (≤6 days): green
 *   - watch (7-8): yellow
 *   - warning (10): orange
 *   - critical / non_compliant (11+): red
 *
 * Returns the Tailwind class fragments the row uses for the pip fill,
 * the count badge background, and the count badge text. Centralised
 * here so the pip strip and the badge can never diverge in colour.
 */
const restDayTone = (consecutiveDays = 0, complianceStatus = null) => {
    // Treat the explicit compliance_status string as authoritative when
    // present — it's set by the daily 07:30 SGT sweep and reflects the
    // backend's tier mapping even if our count-based heuristic would
    // disagree (e.g. when the streak crosses midnight before the sweep
    // re-classifies).
    const tier =
        complianceStatus && complianceStatus !== 'compliant'
            ? complianceStatus
            : consecutiveDays >= 13
                ? 'non_compliant'
                : consecutiveDays >= 12
                    ? 'critical'
                    : consecutiveDays >= 9
                        ? 'warning'
                        : consecutiveDays >= 7
                            ? 'watch'
                            : 'compliant';

    switch (tier) {
        case 'non_compliant':
        case 'critical':
            return {
                pipFill: 'bg-red-500',
                badgeBg: 'bg-red-50',
                badgeText: 'text-red-700',
                badgeRing: 'ring-red-600/20',
            };
        case 'warning':
            return {
                pipFill: 'bg-orange-500',
                badgeBg: 'bg-orange-50',
                badgeText: 'text-orange-700',
                badgeRing: 'ring-orange-600/20',
            };
        case 'watch':
            return {
                pipFill: 'bg-yellow-500',
                badgeBg: 'bg-yellow-50',
                badgeText: 'text-yellow-700',
                badgeRing: 'ring-yellow-600/20',
            };
        default:
            return {
                pipFill: 'bg-green-500',
                badgeBg: 'bg-green-50',
                badgeText: 'text-green-700',
                badgeRing: 'ring-green-600/20',
            };
    }
};

// ---------------------------------------------------------------------------
// RestDayPips — 12-pip discrete visual
// ---------------------------------------------------------------------------

/**
 * RestDayPips — discrete 12-day MOM rest-day streak visualisation.
 *
 * We chose pips over a continuous progress bar because consecutive
 * working days are intrinsically discrete (you've either worked day 7
 * or you haven't — there's no "62% of day 7"). The 12-pip strip lets
 * an RO read the streak length at a glance ("eight filled, four
 * empty") in the same way a fuel gauge or a contact-card stars row
 * works — without translating a percentage. The 7th-pip subtle
 * divider marks the formal MOM "watch" threshold; the 12th pip is
 * the wall.
 *
 * Mobile-first: each pip is ~10px wide with a thin gap; the strip
 * fits inside a one-handed thumb-reachable row on a phone.
 */
const RestDayPips = ({ consecutiveDays = 0, complianceStatus = null }) => {
    const filled = Math.min(12, Math.max(0, consecutiveDays));
    const tone = restDayTone(consecutiveDays, complianceStatus);

    return (
        <div
            className="flex items-center gap-0.5"
            role="img"
            aria-label={`${consecutiveDays} of 12 consecutive working days`}
        >
            {Array.from({ length: 12 }).map((_, i) => {
                const isFilled = i < filled;
                // Subtle divider before pip index 6 (the 7th day) and 11
                // (the 12th day — "the wall"). These are the two MOM
                // tier transitions worth signposting visually.
                const showDivider = i === 6 || i === 11;
                return (
                    <React.Fragment key={i}>
                        {showDivider && (
                            <span
                                aria-hidden="true"
                                className="w-px h-3 bg-gray-300 mx-0.5"
                            />
                        )}
                        <span
                            className={`w-2 h-3 rounded-sm transition-colors ${
                                isFilled ? tone.pipFill : 'bg-gray-200'
                            }`}
                        />
                    </React.Fragment>
                );
            })}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Avatar — small initials chip
// ---------------------------------------------------------------------------

const initials = (name = '') =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase() || '?';

const Avatar = ({ name, classification, size = 'md' }) => {
    const sizeClass = size === 'sm' ? 'w-7 h-7 text-[10px]' : 'w-8 h-8 text-xs';
    const ring =
        classification === 'worker'
            ? 'ring-2 ring-emerald-200 bg-emerald-100 text-emerald-800'
            : 'ring-2 ring-blue-200 bg-blue-100 text-blue-800';
    return (
        <div
            className={`flex items-center justify-center font-semibold rounded-full flex-shrink-0 ${sizeClass} ${ring}`}
            aria-hidden="true"
        >
            {initials(name)}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Row click → Individual tab deep-link (existing pattern)
// ---------------------------------------------------------------------------

const RowButton = React.forwardRef(({ onClick, children, ariaLabel, isHighlighted = false }, ref) => (
    <button
        ref={ref}
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        // 44px minimum row tap target (DS mobile guideline). The inner
        // ``py-2`` keeps the visible content snug while the line-height
        // + flex alignment delivers the 44px hit area on touch devices.
        //
        // When the row is the deep-link target (?highlight=<staff_id>),
        // overlay an amber ring + soft tint that fades after
        // HIGHLIGHT_DURATION_MS. The ring sits OUTSIDE the row to avoid
        // shifting the row's layout — only the box-shadow grows, so
        // adjacent rows never reflow when the highlight applies / clears.
        className={
            `w-full text-left flex items-start gap-3 px-3 py-2 rounded-lg transition-all duration-300 min-h-[44px] ` +
            (isHighlighted
                ? 'bg-amber-50 ring-2 ring-amber-400 ring-offset-1 shadow-sm focus:outline-none focus:ring-amber-500'
                : 'hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1')
        }
    >
        {children}
        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
    </button>
));
RowButton.displayName = 'RowButton';

// ---------------------------------------------------------------------------
// OT Risk Card
// ---------------------------------------------------------------------------

const DEFAULT_VISIBLE_ROWS = 3;

const OtRiskCard = ({ workers, onViewIndividual, totalWorkersInTeam, highlightId }) => {
    const [expanded, setExpanded] = useState(false);
    const highlightedRowRef = useRef(null);

    const sorted = useMemo(() => {
        return [...workers].sort((a, b) => {
            const aHours = a.current_ot_hours ?? 0;
            const bHours = b.current_ot_hours ?? 0;
            if (aHours !== bHours) return bHours - aHours;
            const aProj = a.projected_eom_ot_hours ?? 0;
            const bProj = b.projected_eom_ot_hours ?? 0;
            return bProj - aProj;
        });
    }, [workers]);

    // Auto-expand the card if the deep-link target is on this card but
    // below the visible fold. Otherwise the "Show N more" CTA would
    // hide the row the email pointed at — exactly the row we want
    // the recipient to see first.
    const highlightedIndex = useMemo(() => {
        if (!highlightId) return -1;
        return sorted.findIndex((w) => w.staff_id === highlightId);
    }, [highlightId, sorted]);

    useEffect(() => {
        if (highlightedIndex >= DEFAULT_VISIBLE_ROWS) {
            setExpanded(true);
        }
    }, [highlightedIndex]);

    // Smooth-scroll the highlighted row into view once it's rendered.
    // The ``block: 'center'`` keeps the row vertically centred in the
    // viewport rather than at the very top, which on mobile would
    // bury it under the sticky tab bar.
    useEffect(() => {
        if (highlightedRowRef.current) {
            try {
                highlightedRowRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            } catch {
                // older browsers — fall back to default
                highlightedRowRef.current.scrollIntoView();
            }
        }
    }, [highlightId, expanded]);

    const visible = expanded ? sorted : sorted.slice(0, DEFAULT_VISIBLE_ROWS);
    const hiddenCount = sorted.length - visible.length;

    // Differentiate "vacuously empty" (no Workers under this RO/HOD —
    // the MOM 72h cap doesn't apply to anyone in scope) from "actually
    // compliant" (Workers exist and none are at risk). The first
    // message is correct-but-noise; the second is a useful "everything
    // is fine" signal. Hiding the message for the no-workers case
    // would create a layout gap, so we keep the card present and just
    // soften the language.
    const hasWorkers = (totalWorkersInTeam ?? 0) > 0;

    return (
        <div className="bg-white rounded-2xl shadow-sm border p-4">
            <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                        <Timer className="w-4 h-4 text-orange-700" />
                    </div>
                    <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900">
                            72h OT Watchlist
                        </h4>
                        <p className="text-[11px] text-gray-500">
                            MOM Employment Act Part IV
                        </p>
                    </div>
                </div>
                {sorted.length > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 ring-1 ring-orange-600/20 flex-shrink-0">
                        {sorted.length}
                    </span>
                )}
            </div>

            {sorted.length === 0 ? (
                <div
                    className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2.5 ${
                        hasWorkers
                            ? 'text-emerald-700 bg-emerald-50'
                            : 'text-gray-500 bg-gray-50'
                    }`}
                >
                    {hasWorkers ? (
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    ) : (
                        <Timer className="w-4 h-4 flex-shrink-0 text-gray-400" />
                    )}
                    <span>
                        {hasWorkers
                            ? 'All workers tracking under 48h this month.'
                            : 'No workers in this team — MOM 72h cap does not apply.'}
                    </span>
                </div>
            ) : (
                <>
                    <div className="space-y-1">
                        {visible.map((w) => {
                            const days = daysUntil(w.projected_breach_date);
                            const hasBreach = days != null;
                            const isHighlighted = highlightId === w.staff_id;
                            return (
                                <RowButton
                                    key={w.staff_id}
                                    ref={isHighlighted ? highlightedRowRef : undefined}
                                    onClick={() => onViewIndividual?.(w.staff_id)}
                                    ariaLabel={`Open OT detail for ${w.full_name}`}
                                    isHighlighted={isHighlighted}
                                >
                                    <Avatar name={w.full_name} classification={w.classification} size="sm" />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                            {w.full_name}
                                        </p>
                                        <div className="mt-1">
                                            <OvertimeProgressBar
                                                hours={w.current_ot_hours ?? 0}
                                                projectedHours={w.projected_eom_ot_hours}
                                                compact
                                            />
                                        </div>
                                    </div>
                                    {hasBreach && (
                                        <span
                                            className={`text-[11px] font-semibold whitespace-nowrap px-1.5 py-0.5 rounded flex-shrink-0 ${
                                                days <= 3
                                                    ? 'text-red-700 bg-red-50'
                                                    : days <= 7
                                                        ? 'text-orange-700 bg-orange-50'
                                                        : 'text-amber-700 bg-amber-50'
                                            }`}
                                            title={`Projected to cross 72h on ${formatDate(w.projected_breach_date)}`}
                                        >
                                            {days === 0 ? 'Today' : `${days}d`}
                                        </span>
                                    )}
                                </RowButton>
                            );
                        })}
                    </div>
                    {hiddenCount > 0 && (
                        <button
                            type="button"
                            onClick={() => setExpanded(true)}
                            className="w-full text-center text-xs font-medium text-blue-700 hover:text-blue-900 mt-2 py-1.5 rounded-lg hover:bg-blue-50"
                        >
                            Show {hiddenCount} more
                        </button>
                    )}
                    {expanded && sorted.length > DEFAULT_VISIBLE_ROWS && (
                        <button
                            type="button"
                            onClick={() => setExpanded(false)}
                            className="w-full text-center text-xs font-medium text-gray-500 hover:text-gray-700 mt-2 py-1.5 rounded-lg hover:bg-gray-50"
                        >
                            Show less
                        </button>
                    )}
                </>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Rest Day Card
// ---------------------------------------------------------------------------

const RestDayCard = ({ members, onViewIndividual, highlightId }) => {
    const [expanded, setExpanded] = useState(false);
    const highlightedRowRef = useRef(null);

    const sorted = useMemo(() => {
        return [...members].sort(
            (a, b) =>
                (b.consecutive_working_days ?? 0) - (a.consecutive_working_days ?? 0),
        );
    }, [members]);

    // Same auto-expand + scroll pattern as ``OtRiskCard`` — see that
    // component's effects for the rationale. The deep-link target may
    // land on either card; the watchlist owner doesn't know which, so
    // both cards run identical handlers and at most one will resolve a
    // matching row.
    const highlightedIndex = useMemo(() => {
        if (!highlightId) return -1;
        return sorted.findIndex((m) => m.staff_id === highlightId);
    }, [highlightId, sorted]);

    useEffect(() => {
        if (highlightedIndex >= DEFAULT_VISIBLE_ROWS) {
            setExpanded(true);
        }
    }, [highlightedIndex]);

    useEffect(() => {
        if (highlightedRowRef.current) {
            try {
                highlightedRowRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            } catch {
                highlightedRowRef.current.scrollIntoView();
            }
        }
    }, [highlightId, expanded]);

    const visible = expanded ? sorted : sorted.slice(0, DEFAULT_VISIBLE_ROWS);
    const hiddenCount = sorted.length - visible.length;

    return (
        <div className="bg-white rounded-2xl shadow-sm border p-4">
            <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                        <CalendarDays className="w-4 h-4 text-red-700" />
                    </div>
                    <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900">
                            12-Day Rest Day Watchlist
                        </h4>
                        <p className="text-[11px] text-gray-500">
                            MOM Employment Act s36
                        </p>
                    </div>
                </div>
                {sorted.length > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 ring-1 ring-red-600/20 flex-shrink-0">
                        {sorted.length}
                    </span>
                )}
            </div>

            {sorted.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2.5">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    <span>No rest-day risk — everyone under 7 consecutive days.</span>
                </div>
            ) : (
                <>
                    <div className="space-y-1">
                        {visible.map((m) => {
                            const days = m.consecutive_working_days ?? 0;
                            const tone = restDayTone(days, m.rest_day_compliance);
                            const isHighlighted = highlightId === m.staff_id;
                            return (
                                <RowButton
                                    key={m.staff_id}
                                    ref={isHighlighted ? highlightedRowRef : undefined}
                                    onClick={() => onViewIndividual?.(m.staff_id)}
                                    ariaLabel={`Open rest-day detail for ${m.full_name}`}
                                    isHighlighted={isHighlighted}
                                >
                                    <Avatar name={m.full_name} classification={m.classification} size="sm" />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                            {m.full_name}
                                        </p>
                                        <div className="mt-1 flex items-center gap-2">
                                            <RestDayPips
                                                consecutiveDays={days}
                                                complianceStatus={m.rest_day_compliance}
                                            />
                                            <span className="text-[11px] text-gray-500 truncate">
                                                {m.streak_start_date
                                                    ? `since ${formatDate(m.streak_start_date)}`
                                                    : `${days}d`}
                                            </span>
                                        </div>
                                    </div>
                                    <span
                                        className={`text-[11px] font-semibold whitespace-nowrap px-1.5 py-0.5 rounded-full ring-1 ring-inset flex-shrink-0 ${tone.badgeBg} ${tone.badgeText} ${tone.badgeRing}`}
                                    >
                                        {days}d
                                    </span>
                                </RowButton>
                            );
                        })}
                    </div>
                    {hiddenCount > 0 && (
                        <button
                            type="button"
                            onClick={() => setExpanded(true)}
                            className="w-full text-center text-xs font-medium text-blue-700 hover:text-blue-900 mt-2 py-1.5 rounded-lg hover:bg-blue-50"
                        >
                            Show {hiddenCount} more
                        </button>
                    )}
                    {expanded && sorted.length > DEFAULT_VISIBLE_ROWS && (
                        <button
                            type="button"
                            onClick={() => setExpanded(false)}
                            className="w-full text-center text-xs font-medium text-gray-500 hover:text-gray-700 mt-2 py-1.5 rounded-lg hover:bg-gray-50"
                        >
                            Show less
                        </button>
                    )}
                </>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Top-level — ComplianceWatchlist section
// ---------------------------------------------------------------------------

/**
 * ComplianceWatchlist — real-time visibility for ROs/HODs/Managers
 * on the two MOM compliance signals their workers carry:
 *
 *   - **72-hour monthly OT cap** (Employment Act Part IV).
 *   - **12-day consecutive rest-day limit** (Employment Act s36).
 *
 * Renders two stacked cards on mobile (one column) and side-by-side
 * cards on tablet/desktop (two columns). Each card lists the top
 * {@link DEFAULT_VISIBLE_ROWS} at-risk members; expand-in-place
 * reveals the rest without leaving the page.
 *
 * Rows are tap-to-deep-link via {@link onViewIndividual} — the same
 * callback the existing TeamRollCall MemberCard already uses, so the
 * navigation pattern stays consistent across the Overview tab.
 *
 * Empty state: both cards render even when there are zero alerts, so
 * ROs always see the monitoring surface (predictable layout >
 * conditional hiding for at-a-glance dashboards).
 *
 * @param {Array} members - Per-member rows from the RO/HOD overview
 *   payload. Filtering for eligibility happens inside this component
 *   so callers never need to know the threshold semantics.
 * @param {Function} onViewIndividual - Receives a ``staff_id`` when a
 *   row is tapped. Same contract as the existing MemberCard tap.
 */
export default function ComplianceWatchlist({ members = [], onViewIndividual }) {
    const highlightId = useHighlightedStaffId();

    const otWorkers = useMemo(
        () => members.filter(isOtWatchlistMember),
        [members],
    );
    const restDayMembers = useMemo(
        () => members.filter(isRestDayWatchlistMember),
        [members],
    );
    const totalWorkersInTeam = useMemo(
        () => members.filter((m) => m.classification === 'worker').length,
        [members],
    );

    const totalAlerts = otWorkers.length + restDayMembers.length;

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4 text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900">
                        Compliance Watchlist
                    </h3>
                </div>
                {totalAlerts > 0 ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                        <AlertTriangle className="w-3 h-3" />
                        {totalAlerts} active alert{totalAlerts !== 1 ? 's' : ''}
                    </span>
                ) : (
                    <span className="text-[11px] text-emerald-700 font-medium">
                        All clear
                    </span>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <OtRiskCard
                    workers={otWorkers}
                    onViewIndividual={onViewIndividual}
                    totalWorkersInTeam={totalWorkersInTeam}
                    highlightId={highlightId}
                />
                <RestDayCard
                    members={restDayMembers}
                    onViewIndividual={onViewIndividual}
                    highlightId={highlightId}
                />
            </div>
        </div>
    );
}

export {
    isOtWatchlistMember,
    isRestDayWatchlistMember,
    RestDayPips,
    restDayTone,
};
